<template>
  <form @submit="onSubmit" class="flex flex-col gap-4 relative">
    <FormField v-slot="{ value, handleChange, componentField }" name="email">
      <FormItem>
        <FormLabel class="text-colorGrey4">Email</FormLabel>
        <FormControl>
          <Input
            class="text-white bg-colorGrey2 disabled:opacity-100"
            v-bind="componentField"
            type="email"
            disabled
            
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="firstName">
      <FormItem>
        <FormLabel class="text-colorGrey4">{{
          t("signup.firstName")
        }}</FormLabel>
        <FormControl>
          <Input :placeholder="t('signup.firstName')" v-bind="componentField" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="lastName">
      <FormItem>
        <FormLabel class="text-colorGrey4">
          {{ t("signup.lastName") }}
        </FormLabel>
        <FormControl>
          <Input v-bind="componentField" :placeholder="t('signup.lastName')" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="password">
      <FormItem>
        <FormLabel class="text-colorGrey4">{{
          t("signup.password")
        }}</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter your password"
              v-bind="componentField"
              v-model="password"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2"
              @click="showPassword = !showPassword"
            >
              <Icon
                :name="!showPassword ? 'ph:eye-slash' : 'ph:eye'"
                class="h-4 w-4"
              />
            </button>
          </div>
        </FormControl>
        <FormMessage />
        <div v-if="hasPasswordInput" class="flex flex-col gap-2 text-sm">
          <div
            :class="[
              'flex items-center gap-2',
              passwordValidation.hasUpperCase
                ? 'text-green-500'
                : 'text-red-500',
            ]"
          >
            <Icon
              :name="passwordValidation.hasUpperCase ? 'ph:check' : 'ph:x'"
            />
            <span>One uppercase letter</span>
          </div>
          <div
            :class="[
              'flex items-center gap-2',
              passwordValidation.hasLowerCase
                ? 'text-green-500'
                : 'text-red-500',
            ]"
          >
            <Icon
              :name="passwordValidation.hasLowerCase ? 'ph:check' : 'ph:x'"
            />
            <span>One lowercase letter</span>
          </div>
          <div
            :class="[
              'flex items-center gap-2',
              passwordValidation.hasNumber ? 'text-green-500' : 'text-red-500',
            ]"
          >
            <Icon :name="passwordValidation.hasNumber ? 'ph:check' : 'ph:x'" />
            <span>One number</span>
          </div>
          <div
            :class="[
              'flex items-center gap-2',
              passwordValidation.hasMinLength
                ? 'text-green-500'
                : 'text-red-500',
            ]"
          >
            <Icon
              :name="passwordValidation.hasMinLength ? 'ph:check' : 'ph:x'"
            />
            <span>8 characters minimum</span>
          </div>
          <div
            :class="[
              'flex items-center gap-2',
              passwordValidation.hasMaxLength
                ? 'text-green-500'
                : 'text-red-500',
            ]"
          >
            <Icon
              :name="passwordValidation.hasMaxLength ? 'ph:check' : 'ph:x'"
            />
            <span>20 characters maximum</span>
          </div>
        </div>
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="confirmPassword">
      <FormItem>
        <FormLabel class="text-colorGrey4">{{ t("signup.confirmPassword") }}</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="Confirm your password"
              v-model="confirmPassword"
              v-bind="componentField"
            />
            <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <Icon
                  :name="!showConfirmPassword ? 'ph:eye-slash' : 'ph:eye'"
                  class="h-4 w-4"
                />
              </button>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="country">
      <FormItem>
        <FormLabel class="text-colorGrey4">{{ t("signup.country") }}</FormLabel>
        <Select v-bind="componentField">
          <SelectTrigger>
            <SelectValue placeholder="Select a country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="country in countryList" :key="country.id" :value="country.countryCode">
              {{ country.country }}
            </SelectItem>

          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    </FormField>    
    
    <FormField v-slot="{ value, handleChange }" type="checkbox" name="terms" >
      <FormItem class="flex flex-row items-start space-x-3 space-y-0">
        <FormControl>
          <Checkbox :model-value="value" @update:model-value="handleChange" />
        </FormControl>
        <div class="flex flex-col gap-1 leading-none text-colorGrey5">
          <FormLabel class="text-colorGrey4">
            I accept the
            <a :href="`${config.public.TermsOfServiceUrl}`" target="_blank" class="text-white underline hover:underline"
              >{{ t("signup.TermsOfService") }}</a
            >
            <span class="text-white"> & </span>
            <a :href="`${config.public.PrivacyUrl}`" target="_blank" class="text-white underline hover:underline"
              >{{ t("signup.privacyPolicy") }}</a
            >
          </FormLabel>
          <FormMessage />
        </div>
      </FormItem>
    </FormField>
    <Button type="submit" class="w-full">Sign up</Button>
    {{ pValue }}
  </form>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useForm, useField } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as z from "zod";
import { useI18n } from "vue-i18n";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const props = withDefaults(defineProps<{
  emailValue: string;
  emailToken: string;
}>(), {
  emailValue: '',
  emailToken: ''
});


const { t, locale } = useI18n();
const config = useRuntimeConfig();
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const password = ref("");
const confirmPassword = ref("");
const hasPasswordInput = ref(false); 

const { countries: countryList, fetchCountries } = useCountries();

// 密碼驗證函數
// const { value } = useField("password")
const passwordValidation = computed(() => {
  const passwordValue = password.value;
  return {
    hasUpperCase: /[A-Z]/.test(passwordValue),
    hasLowerCase: /[a-z]/.test(passwordValue),
    hasNumber: /[0-9]/.test(passwordValue),
    hasMinLength: passwordValue.length >= 8,
    hasMaxLength: passwordValue.length <= 20,
  };
});



// 檢查所有密碼條件是否都滿足
const isPasswordValid = computed(() =>
  Object.values(passwordValidation.value).every(Boolean)
);

// 監聽密碼輸入，當使用者開始輸入時顯示驗證規則
watch(password, (newValue) => {
  hasPasswordInput.value = true;
});

onMounted(async() => {
  // 載入國家列表
  await fetchCountries();
})

// 表單驗證 schema
const formSchema = toTypedSchema(
  z
    .object({
      email: z.string({message: "Please enter a valid email"}).email({message: "Please enter a valid email"}),
      firstName: z.string({message: "First name is required"}).min(1, {message: "First name is required"}),
      lastName: z.string({message: "Last name is required"}).min(1, {message: "Last name is required"}),
      password: z
        .string()
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/[a-z]/, "Password must contain at least one lowercase letter")
        .regex(/[0-9]/, "Password must contain at least one number")
        .min(8, "Password must be at least 8 characters")
        .max(20, "Password must not exceed 20 characters"),
      confirmPassword: z.string({message: "Confirm password is required"}).min(1, {message: "Confirm password is required"}),
      country: z.string().min(1, {message: "Please select a country"}),
      // terms: z.boolean().refine((val) => val === true, {message: "You must accept the terms and conditions"}),
      terms: z.boolean().refine((val) => val === true, {message: "You must accept the terms and conditions"}),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "Passwords don't match",
      path: ["confirmPassword"],
    })
);
const { handleSubmit } = useForm({
  validationSchema: formSchema,
  initialValues: {
    email: props.emailValue,
    terms: false,
  }
});

const route = useRoute();
const pValue = computed(()=> {
  const p = route.query.p;
  return typeof p === 'string' ? `?p=${p}` : ''; 
})

const onSubmit = handleSubmit(async(values) => {
  try{
    // post 
    await $fetch(`${config.public.APIurl}/api/v3/gsn/register${pValue.value}`, {
      method: 'POST',
      body: {
        "lang": locale.value,
        "userMail": values.email,
        "userPwd": values.password,
        "userPwdConfirm": values.confirmPassword,
        "firstName": values.firstName,
        "lastName": values.lastName,
        "countryCode": values.country,
        "emailToken": props.emailToken
      }
    })
    return navigateTo(`${config.public.GSNurl}/auth/login/`, {external: true});
  } catch (error:unknown) {
    console.error(error);
  } 
  if (!isPasswordValid.value) {
    return;
  }
  // 處理表單提交
});
</script>
